import {
  getV1CompaniesCompanyIdOrdersDispatch,
  GetV1CompaniesCompanyIdOrdersDispatchData,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';

import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';

import { Order } from './models';

export default function useOrdersAssigned() {
  const { ordersAssignedStore } = useStores();

  const getCompanyOrdersByCompanyIdCancelToken = useCancelToken();
  const getCompanyOrdersByCompanyId = async ({ companyId }: { companyId: string }) => {
    const { pagination, filters } = ordersAssignedStore;

    const query: NonNullable<GetV1CompaniesCompanyIdOrdersDispatchData['query']> = {
      'page[limit]': pagination.limit,
    };

    if (pagination.before) {
      query['page[before]'] = pagination.before;
    } else if (pagination.after) {
      query['page[after]'] = pagination.after;
    }

    if (filters?.search?.trim().length) {
      query['search[job][datagrid]'] = filters.search.trim();
    }

    if (filters?.customerAccountIds?.length) {
      query['filter[job][customer_account_ids]'] = filters.customerAccountIds;
    }

    if (filters?.dispatchNumberIds?.length) {
      query['filter[dispatch_number_ids]'] = filters.dispatchNumberIds;
    }

    if (filters?.driverIds?.length) {
      query['filter[job][driver_ids]'] = filters.driverIds;
    }

    if (filters?.projectsExternalIds?.length) {
      query['filter[job][external_ids]'] = filters.projectsExternalIds;
    }

    if (filters?.projectIds?.length) {
      query['filter[job][project_ids]'] = filters.projectIds;
    }

    if (filters?.vendorAccountIds?.length) {
      query['filter[job][vendor_account_ids]'] = filters.vendorAccountIds;
    }

    if (filters?.orderStates?.length) {
      query['filter[states]'] = filters.orderStates;

      if (filters?.refinedOrderStates?.length) {
        query['filter[states]'] = filters.refinedOrderStates;
      }
    }

    if (filters?.startDate?.length) {
      query['filter[job][start_date]'] = dayjs
        .tz(filters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (filters?.endDate?.length) {
      query['filter[job][end_date]'] = dayjs
        .tz(filters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (filters?.jobStates?.length) {
      query['filter[job][states]'] = filters.jobStates;
    }

    if (filters?.materialIds?.length) {
      query['filter[job][material_ids]'] = filters.materialIds;
    }

    if (filters?.pickUpSites?.length) {
      query['filter[job][pickup_site_ids]'] = filters.pickUpSites;
    }

    if (filters?.dropoffSites?.length) {
      query['filter[job][dropoff_site_ids]'] = filters.dropoffSites;
    }

    if (filters?.requesterIds?.length) {
      query['filter[requester_ids]'] = filters.requesterIds;
    }

    if (filters?.siteTypes?.length) {
      query['filter[job][site_types]'] = filters.siteTypes;
    }

    if (filters?.equipmentTypes?.length) {
      query['filter[equipment_type_ids]'] = filters.equipmentTypes;
    }

    if (filters?.foremen?.length) {
      query['filter[job][foreman_ids]'] = filters.foremen;
    }

    if (filters?.departmentIds?.length) {
      query['filter[department_ids]'] = filters.departmentIds;
    }

    if (filters?.collaborators?.length) {
      query['filter[job][collaborator_ids]'] = filters.collaborators;
    }

    if (filters?.salespeople?.length) {
      query['filter[job][salesperson_ids]'] = filters.salespeople;
    }

    if (filters?.aiReviewStatuses?.length) {
      query['filter[job][ai_review][statuses]'] = filters.aiReviewStatuses;
    }

    if (filters?.prevailingWage?.length === 1) {
      query['filter[job][prevailing_wage]'] = filters.prevailingWage[0] === 'true';
    }

    if (filters?.ticketPotentialDuplicate?.length) {
      query['filter[job][ticket][potential_duplicate]'] =
        filters.ticketPotentialDuplicate[0] === 'true';
    }

    if (filters?.ticketTypes?.length === 1) {
      query['filter[default_digital_ticket_view]'] = filters.ticketTypes[0] === 'true';
    }

    ordersAssignedStore.ordersFetchStart();
    const cancelToken = getCompanyOrdersByCompanyIdCancelToken.issueToken();

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });
      const orders = response.data.data.map((order) => Order.parse(order));
      const pagination = extractPagination(response);
      ordersAssignedStore.ordersFetchEnd(orders, pagination);
    } catch (error) {
      if (!axios.isCancel(error)) {
        ordersAssignedStore.ordersFetchEnd([]);
        connection.handleRequestError(error, t('error_messages.orders.failed_to_fetch'));
      }
    } finally {
      getCompanyOrdersByCompanyIdCancelToken.discardToken();
    }
  };

  return { getCompanyOrdersByCompanyId };
}
